import type { ReactNode } from 'react'
import React, { useState } from 'react'
import { Drawer, breakpointQueries, Button, Modal, useMediaQuery } from '@systemeio/components'
import { XCloseIcon } from '@systemeio/icons'
import type { ParseKeys } from 'i18next'
import { useTranslation } from 'next-i18next/pages'
import { UnprocessableContentError } from 'shared/errors/unprocessable-content-error'
import { BadRequest } from '../errors/bad-request'
import { InternalError } from '../errors/internal-error'

interface ConfirmationProps {
  onCloseModal: () => void
  onConfirm: () => void
  title?: ParseKeys
  confirmationContent?: React.ReactNode
  errorMessageMap?: Partial<Record<'internalError' | 'unprocessableContentError', ParseKeys>>
}

function Confirmation({
  onCloseModal,
  onConfirm,
  title = 'confirmation_modal.default_title',
  confirmationContent = '',
  errorMessageMap,
}: ConfirmationProps): ReactNode {
  const { t } = useTranslation()
  const [opened, setOpened] = useState(true)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const isMobile = useMediaQuery(breakpointQueries.isMobile, { initializeWithValue: false })

  function closeModal(): void {
    setOpened(false)
    onCloseModal()
  }

  const handleConfirm = async (): Promise<void> => {
    try {
      setIsLoading(true)
      await onConfirm()
      setOpened(false)
    } catch (error_) {
      if (error_ instanceof BadRequest && error_.errors.common) {
        setError(error_.errors.common.join(''))
      } else if (
        error_ instanceof UnprocessableContentError &&
        errorMessageMap?.unprocessableContentError
      ) {
        setError(t(errorMessageMap.unprocessableContentError))
      } else if (error_ instanceof InternalError) {
        setError(t(errorMessageMap?.internalError ?? 'core.error.internal_error_message'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (!isMobile) {
    return (
      <Modal.Root
        open={opened}
        onOpenChange={open => {
          if (!open) closeModal()
        }}
      >
        <Modal.Popup>
          <Modal.Header>
            <Modal.Title>{t(title)}</Modal.Title>
          </Modal.Header>
          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-8 py-6">
            <div className="flex justify-center">{confirmationContent}</div>
            {error && <p className="text-center text-sm text-text-error">{error}</p>}
          </div>
          <Modal.Footer>
            <Button variant="secondary" onClick={onCloseModal}>
              {t('confirmation_modal.cancel')}
            </Button>
            <Button
              data-testid="confirmation-confirm"
              variant="primary"
              isLoading={isLoading}
              onClick={handleConfirm}
            >
              {t('confirmation_modal.confirm')}
            </Button>
          </Modal.Footer>
        </Modal.Popup>
      </Modal.Root>
    )
  }

  return (
    <Drawer.Root
      open={opened}
      onOpenChange={open => {
        if (!open) closeModal()
      }}
    >
      <Drawer.Popup>
        <Drawer.Header className="flex-row items-start gap-2.5">
          <Drawer.Title className="min-w-0 flex-1">{t(title)}</Drawer.Title>
          <Drawer.Close
            aria-label="Close"
            className="shrink-0"
            render={<Button variant="ghost-gray" size="icon-sm" />}
          >
            <XCloseIcon />
          </Drawer.Close>
        </Drawer.Header>
        <Drawer.Content>
          <div className="flex justify-center">{confirmationContent}</div>
          {error && <p className="text-center text-sm text-text-error">{error}</p>}
        </Drawer.Content>
        <Drawer.Footer>
          <Button variant="secondary" onClick={onCloseModal}>
            {t('confirmation_modal.cancel')}
          </Button>
          <Button
            data-testid="confirmation-confirm"
            variant="primary"
            isLoading={isLoading}
            onClick={handleConfirm}
          >
            {t('confirmation_modal.confirm')}
          </Button>
        </Drawer.Footer>
      </Drawer.Popup>
    </Drawer.Root>
  )
}

export default Confirmation
